import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import { isDecisionMaker } from './decisionMaker';
import { fetchGammaPetitionDetailsExperiments } from './experiments';
import { isGdprConsentProvided } from './gdprConsentProvided';
import type { PetitionViewerInfo } from './petitionViewerInfo';
import { getPetitionViewerInfo } from './petitionViewerInfo';
import { isUserEligibleForLegislativeBodies } from './userEligibleForLegislativeBodies';

export type PetitionDetailsPageUserData = PetitionViewerInfo & {
	decisionMaker: boolean;
	gdprConsentProvided: boolean;
	signatureThermometerEnabled: boolean;
	isSunriseEnabled: boolean;
	shareVisualsExperimentEnabled: boolean;
	isMobileAppDownloadBannerEnabled: boolean;
	fullSunrisePetitionPageShareComponentExperiment: boolean;
	isUserEligibleForLegislativeBodies: boolean;
};

export async function getPetitionDetailsPageUserData(
	utilityContext: UtilityContext,
	slugOrId: string,
	{
		currentUserId,
		isGuest,
		decisionMakerHash,
		hasShowLegislativeBodiesParam,
	}: {
		currentUserId: string | undefined;
		isGuest: boolean;
		decisionMakerHash?: string;
		hasShowLegislativeBodiesParam: boolean;
	},
): Promise<PetitionDetailsPageUserData> {
	const [petitionViewerInfo, decisionMaker, gdprConsentProvided, experiments, userEligibleForLegislativeBodies] =
		await Promise.all([
			getPetitionViewerInfo(utilityContext, slugOrId, currentUserId),
			!isGuest || decisionMakerHash
				? isDecisionMaker(utilityContext, { slugOrId, decisionMakerHash })
				: Promise.resolve(false),
			isGdprConsentProvided(utilityContext),
			fetchGammaPetitionDetailsExperiments(utilityContext),
			isUserEligibleForLegislativeBodies(utilityContext, {
				hasShowLegislativeBodiesParam,
			}),
		]);

	const {
		sunriseSupportersV1,
		supporterShareVisualsExperiment,
		signatureThermometerExperimentDesktop,
		fullSunrisePetitionPageShareComponentExperiment,
	} = experiments;

	return {
		...petitionViewerInfo,
		decisionMaker,
		gdprConsentProvided,
		signatureThermometerEnabled: signatureThermometerExperimentDesktop.variation === 'v1',
		isSunriseEnabled: sunriseSupportersV1.variation === 'enabled',
		shareVisualsExperimentEnabled: supporterShareVisualsExperiment.variation === 'v1',
		isMobileAppDownloadBannerEnabled: experiments.mobileAppDownloadBanner.variation === 'enabled',
		fullSunrisePetitionPageShareComponentExperiment:
			fullSunrisePetitionPageShareComponentExperiment.variation === 'enabled',
		isUserEligibleForLegislativeBodies: userEligibleForLegislativeBodies,
	};
}
